import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Navbar, Footer } from '../components';
import api from '../utils/api';

const RoleRequest = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [existingRequest, setExistingRequest] = useState(null);
  
  const [requestType, setRequestType] = useState('seller'); // 'seller' or 'admin'
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    store_name: '',
    store_address: '',
    employee_id: '',
    department: '',
    phone_number: user?.phone || '',
    npwp: '',
    social_media: '',
    verification_document: null,
    approval_document: null
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    
    if (user?.role === 'seller' || user?.role === 'admin' || user?.role === 'super_admin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }

    fetchMyRequest();
  }, [isAuthenticated, navigate, user]);

  const fetchMyRequest = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/request-role/my');
      setExistingRequest(res.data); // could be null if no request
    } catch (err) {
      console.log("Belum ada request atau error", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, field) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, [field]: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.verification_document) {
      return toast.error("Dokumen Identitas wajib diunggah.");
    }
    if (requestType === 'admin' && !formData.approval_document) {
      return toast.error("Surat persetujuan atasan wajib diunggah untuk role Admin.");
    }
    if (requestType === 'seller' && !formData.approval_document) {
      return toast.error("Surat Pernyataan wajib diunggah untuk role Seller.");
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('requested_role', requestType);
      fd.append('reason', formData.reason);
      fd.append('phone_number', formData.phone_number);
      
      if (requestType === 'seller') {
        fd.append('store_name', formData.store_name);
        fd.append('store_address', formData.store_address);
        if (formData.npwp) fd.append('npwp', formData.npwp);
        if (formData.social_media) fd.append('social_media', formData.social_media);
      } else {
        fd.append('employee_id', formData.employee_id);
        fd.append('department', formData.department);
      }
      
      fd.append('verification_document', formData.verification_document);
      if (formData.approval_document) {
        fd.append('approval_document', formData.approval_document);
      }

      await api.post('/api/request-role', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Pengajuan berhasil dikirim!");
      fetchMyRequest(); // Refresh status
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal mengirim pengajuan");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white py-12 px-4 sm:px-6 lg:px-8 selection:bg-emerald-500/30">
        <div className="max-w-3xl mx-auto">
          
          <div className="mb-8 text-center sm:text-left flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Pengajuan Peran / Toko</h1>
              <p className="mt-2 text-zinc-400">Bergabunglah menjadi bagian dari ekosistem kami sebagai Penjual atau Admin.</p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-zinc-900/50 shadow-xl overflow-hidden">
            {!loading && (!existingRequest || existingRequest.status === 'rejected') && (
              <div className="flex border-b border-white/10 bg-zinc-950/50">
                <button
                  type="button"
                  onClick={() => setRequestType('seller')}
                  className={`flex-1 py-4 text-sm font-bold transition ${requestType === 'seller' ? 'bg-emerald-500/10 text-emerald-400 border-b-2 border-emerald-400' : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'}`}
                >
                  🚀 Mulai Berjualan (Seller)
                </button>
                <button
                  type="button"
                  onClick={() => setRequestType('admin')}
                  className={`flex-1 py-4 text-sm font-bold transition ${requestType === 'admin' ? 'bg-indigo-500/10 text-indigo-400 border-b-2 border-indigo-400' : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'}`}
                >
                  🛡️ Daftar sebagai Staff (Admin)
                </button>
              </div>
            )}

            <div className="p-6 sm:p-10">
              {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-800 border-t-emerald-500"></div>
              </div>
            ) : existingRequest && existingRequest.status === 'pending' ? (
              <div className="text-center py-10">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/10 mb-6 ring-1 ring-amber-500/20">
                  <svg className="h-10 w-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Pengajuan Sedang Diproses</h3>
                <p className="text-zinc-400 max-w-md mx-auto mb-6">
                  Terima kasih! Tim kami sedang memverifikasi data Anda. Proses ini biasanya memakan waktu 1x24 jam kerja.
                </p>
                <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-4 text-left gap-6">
                   <div>
                     <p className="text-xs font-semibold text-zinc-500 mb-1">Peran Diajukan</p>
                     <p className="font-bold text-white uppercase">{existingRequest.requested_role}</p>
                   </div>
                   {existingRequest.requested_role === 'seller' && (
                     <div>
                       <p className="text-xs font-semibold text-zinc-500 mb-1">Nama Toko Diajukan</p>
                       <p className="font-bold text-white">{existingRequest.store_name}</p>
                     </div>
                   )}
                </div>
              </div>
            ) : (
              <div>
                {existingRequest && existingRequest.status === 'rejected' && (
                  <div className="mb-8 rounded-2xl bg-rose-500/10 border border-rose-500/20 p-5 p">
                    <div className="flex items-start gap-4">
                      <svg className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-bold text-rose-500">Pengajuan Sebelumnya Ditolak</h4>
                        <p className="text-sm text-rose-300 mt-1">{existingRequest.admin_note || 'Data tidak lengkap atau tidak valid.'}</p>
                        <p className="text-xs text-rose-400 mt-2">Silakan perbaiki data Anda dan ajukan ulang menggunakan formulir di bawah ini.</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">Informasi Pengajuan</h3>
                      
                      {requestType === 'seller' ? (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Toko <span className="text-rose-500">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.store_name}
                              onChange={e => setFormData({...formData, store_name: e.target.value})}
                              placeholder="Contoh: Maju Jaya Store"
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Alamat Lengkap Toko <span className="text-rose-500">*</span></label>
                            <textarea
                              required
                              rows="3"
                              value={formData.store_address}
                              onChange={e => setFormData({...formData, store_address: e.target.value})}
                              placeholder="Jalan, RT/RW, Kelurahan, Kecamatan, Kota..."
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none resize-none"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Nomor Pegawai (Employee ID) <span className="text-rose-500">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.employee_id}
                              onChange={e => setFormData({...formData, employee_id: e.target.value})}
                              placeholder="Contoh: EMP-2023001"
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Departemen <span className="text-rose-500">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.department}
                              onChange={e => setFormData({...formData, department: e.target.value})}
                              placeholder="Contoh: Customer Service"
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                        </>
                      )}

                      <div>
                         <label className="block text-sm font-semibold text-zinc-300 mb-2">Alasan Pengajuan <span className="text-rose-500">*</span></label>
                         <textarea
                           required
                           rows="2"
                           value={formData.reason}
                           onChange={e => setFormData({...formData, reason: e.target.value})}
                           placeholder={requestType === 'seller' ? "Jelaskan jenis produk yang akan dijual..." : "Jelaskan untuk keperluan apa akses Admin ini..."}
                           className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none resize-none"
                         />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">Identitas & Kontak</h3>
                      
                      <div>
                        <label className="block text-sm font-semibold text-zinc-300 mb-2">No. WhatsApp <span className="text-rose-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={formData.phone_number}
                          onChange={e => setFormData({...formData, phone_number: e.target.value})}
                          placeholder="08123456789"
                          className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                        />
                      </div>

                      {requestType === 'seller' && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">NPWP (Opsional)</label>
                            <input
                              type="text"
                              value={formData.npwp}
                              onChange={e => setFormData({...formData, npwp: e.target.value})}
                              placeholder="Nomor Pokok Wajib Pajak"
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Sosial Media (Opsional)</label>
                            <input
                              type="text"
                              value={formData.social_media}
                              onChange={e => setFormData({...formData, social_media: e.target.value})}
                              placeholder="Link IG / FB / TikTok"
                              className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                        </>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-zinc-300 mb-2">Identity Document (KTP / Company Card) <span className="text-rose-500">*</span></label>
                          <input 
                            type="file" 
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileChange(e, 'verification_document')}
                            className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 transition cursor-pointer"
                          />
                          <p className="mt-1 text-xs text-zinc-500">Allowed: JPG, PNG, PDF. Max 2MB.</p>
                        </div>
                        
                        {requestType === 'admin' ? (
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Supervisor Approval Letter <span className="text-rose-500">*</span></label>
                            <input 
                              type="file" 
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => handleFileChange(e, 'approval_document')}
                              className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 transition cursor-pointer"
                            />
                            <p className="mt-1 mb-2 text-xs text-zinc-500">Allowed: JPG, PNG, PDF. Max 2MB.</p>
                            <a href="https://drive.google.com/drive/folders/1HQ6_dPKLTm2xRed0I1Y1otuZnMLEH6rl?usp=drive_link" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition">
                              Template Surat Persetujuan Atasan Approval Pengajuan Role Admin
                            </a>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-sm font-semibold text-zinc-300 mb-2">Statement Letter (Surat Pernyataan) <span className="text-rose-500">*</span></label>
                            <input 
                              type="file" 
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => handleFileChange(e, 'approval_document')}
                              className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 transition cursor-pointer"
                            />
                            <p className="mt-1 mb-2 text-xs text-zinc-500">Upload a signed statement letter agreeing to terms.</p>
                            <a href="https://drive.google.com/drive/folders/1smcQvJ-HC_Uv42IoYTIQKYJCY2TKK_Uq?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-purple-400 hover:text-purple-300 transition">
                              Template SURAT PERNYATAAN PENGAJUAN SELLER
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 mt-6 border-t border-white/10 flex items-center justify-between">
                    <p className="text-xs text-zinc-500 max-w-sm">
                      Dengan mengirimkan form ini, Anda menyetujui Syarat & Ketentuan yang berlaku di platform ini.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`rounded-xl px-8 py-3.5 text-sm font-extrabold text-white shadow-sm transition disabled:opacity-50 ${requestType === 'admin' ? 'bg-indigo-500 hover:bg-indigo-400' : 'bg-emerald-500 hover:bg-emerald-400'}`}
                    >
                      {submitting ? 'Mengirim Data...' : 'Ajukan Sekarang'}
                    </button>
                  </div>
                </form>
              </div>
            )}
            </div>
          </div>
          
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RoleRequest;
